// Per-(slice, serialized-query) memo cache for `selectByQuery`.
//
// WHY: `selectByQuery` is a single shared reselect selector per entity, but its
// ~109 call sites feed it inline object literals (`{campaignId: x}`, `{deleted:false}`,
// …). reselect's default cache size is 1 and its input-equality is reference-based,
// so a fresh literal every render is never a cache hit — the full `sift()` scan over
// every id re-runs on *every* dispatch, for *every* subscriber, and returns a *fresh*
// array. R3-D's shallowEqual pre-pass suppresses the resulting re-render, but the scan
// itself still runs.
//
// This cache keys results on a stable serialization of (filter, projection, sort) and
// stores the RTK slice reference the result was computed from. RTK/immer guarantees the
// slice object identity changes on (and only on) a mutation to that slice, so
// `cached.slice === currentSlice` is a correct, precise invalidation signal: same slice
// ref + same query string ⇒ the sift result is provably identical ⇒ return the cached
// array (same reference — no re-scan, no fresh-reference churn). When the slice ref
// changes, the entry is stale and recomputed exactly once, then shared by all
// subscribers of that new slice version.
//
// BYPASS: queries containing functions / RegExp / symbol / bigint can't be faithfully
// or uniquely serialized (e.g. `JSON.stringify(/a/) === '{}'`, functions are dropped),
// so `serializeQuery` returns null for them and the caller falls back to the un-cached
// reselect path — never a wrong cache hit. Dates serialize via toJSON to a unique,
// stable ISO string and are safe. Key insertion order follows the call site's object
// literal, which is stable per site; a differently-ordered equivalent query is merely a
// cache miss (recomputed), never a wrong result.

export interface SliceQueryCache<S, R> {
  get(key: string, slice: S): R | undefined;
  set(key: string, slice: S, result: R): void;
}

/**
 * Bounded (LRU) per-entity query-result cache. Instantiated once per
 * `createDBServiceSlice`, so it is already scoped to a single entity/slice.
 */
export function createSliceQueryCache<S, R>(maxEntries = 200): SliceQueryCache<S, R> {
  const map = new Map<string, { slice: S; result: R }>();

  return {
    get(key, slice) {
      const hit = map.get(key);
      // Only a hit when it was computed from the *current* slice reference.
      if (hit !== undefined && hit.slice === slice) {
        // Refresh recency (Map preserves insertion order → re-insert = move to newest).
        map.delete(key);
        map.set(key, hit);
        return hit.result;
      }
      return undefined;
    },
    set(key, slice, result) {
      map.delete(key);
      map.set(key, { slice, result });
      if (map.size > maxEntries) {
        // Evict the oldest (first) entry.
        const oldest = map.keys().next().value;
        if (oldest !== undefined) map.delete(oldest);
      }
    },
  };
}

/**
 * Stable cache key for a query, or `null` when it can't be safely/uniquely
 * serialized (contains a function, RegExp, symbol, or bigint) — in which case
 * the caller MUST bypass the cache.
 */
export function serializeQuery(...parts: unknown[]): string | null {
  let unsafe = false;
  try {
    const str = JSON.stringify(parts, (_key, value) => {
      const t = typeof value;
      if (t === 'function' || t === 'symbol' || t === 'bigint' || value instanceof RegExp) {
        unsafe = true;
      }
      return value;
    });
    // `str === undefined` happens only if the whole thing serialized to nothing.
    return unsafe || str === undefined ? null : str;
  } catch {
    // Circular structure or a throwing toJSON → don't risk a wrong key.
    return null;
  }
}
